import React, { useMemo } from "react";
import isEmpty from "lodash/isEmpty";
import { formatCityLabel } from "utils/formatter";
import StarIcon from "images/star-icon.svg";
import conciergeIcon from "images/concierge-icon.svg";
import { ReactComponent as LocationPin } from "images/location-pin-icon.svg";
import styles from "./LocationDetails.module.css";

const DEFAULT_AVATAR = "https://atmosfy.io/assets/ig-glyph.png";

/**
 * At the time of this component's creation 03-14-2024, ReviewCard is the only component
 * that uses the LocationDetails component. We can move this to a generic component if we need to
 * use it elsewhere.
 * */
const LocationDetails = ({
  location,
  author,
  onAuthorClick = () => {},
  showAddress = false,
  showAuthor = false,
  showTags = false
}) => {
  const {
    clm_address,
    clm_city,
    clm_country,
    clm_name,
    clm_price_level,
    clm_rating,
    clm_state,
    tags
  } = location || {};

  const { clm_username, clm_maven, avatarUrl } = author || {};
  const { cuisines } = tags || {};
  const topCuisines = cuisines?.slice(0, 3);
  const priceLevel = Array(Number(clm_price_level)).fill("$").join("");
  const cityLabel = formatCityLabel(clm_city, clm_state, clm_country);

  const shouldRenderExtraMeta = useMemo(() => {
    return (showAddress && !isEmpty(cuisines)) || !showAddress;
  }, [showAddress, cuisines]);

  return (
    <div className={styles.locationDetailsContainer}>
      <p className={styles.locationName}>
        {clm_name}
      </p>

      {showAddress && (
        <div className={styles.locationMetaContainer}>
          <LocationPin fill="#9FA7AD" style={{ width: 16, height: 16 }} />
          <p className={styles.locationAddress}>
            <span>{`${clm_address}, `}</span>
            <span>{cityLabel}</span>
          </p>
        </div>
      )}

      <div className={styles.locationMetaContainer}>
        {clm_rating && (
          <>
            <span className={styles.locationRatingContainer}>
              <img src={StarIcon} alt="Rating" style={{width: 16, height: 16}}/>
              <span>
                {clm_rating}
              </span>
            </span>
            <span>•</span>
          </>
        )}

        {priceLevel && (
          <>
            <span>{priceLevel}</span>
            {shouldRenderExtraMeta && <span>•</span>}
          </>
        )}

        {shouldRenderExtraMeta && (
          <span>{!showAddress ? cityLabel : "Food & Drink"}</span>
        )}
      </div>
      {showTags && !isEmpty(cuisines) && (
        <div className={styles.locationMetaContainer}>
          {topCuisines.map((cuisine, index) => {
            const isLast = index === topCuisines.length - 1;
            return (
              <span key={index} className={styles.locationTag}>
                {cuisine?.name}
                {isLast ? "" : ", "}
              </span>
            )
          })}
        </div>
      )}
      {showAuthor && !isEmpty(author) && (
        <div
          className={`${styles.locationMetaContainer} ${styles.authorContainer}`}
          onClick={(evt) => {
            evt.stopPropagation();
            onAuthorClick(author)
          }}
        >
          <img
            src={avatarUrl || DEFAULT_AVATAR}
            alt="Profile"
            className={styles.authorAvatar}
            loading="lazy"
            onError={({currentTarget}) => {
              currentTarget.onerror = null; // prevents looping
              currentTarget.src = DEFAULT_AVATAR;
            }}
          />
          <div className={styles.usernameContainer}>
            <span className={styles.username}>{clm_username}</span>
            {clm_maven === "1" && (
              <img
                className={styles.conciergeIcon}
                src={conciergeIcon}
                alt="Concierge Badge"
              />)}
          </div>
        </div>
      )}
    </div>
  )
}

export default LocationDetails;
